import { Map } from 'features/mapping/components/Map';
import { MapType } from 'features/mapping/mapTypePolicy';
import { UseMarkerEditLockResult } from 'features/geoEvents/hooks/useMarkerEditLock';
import {
  getWildfireEventScale,
  getWildfireGeoEventIcon,
} from 'features/mapping/icons';
import { iconFootprint } from 'features/mapping/components/MarkerEditConnectorLine';
import { MarkerEditControls } from 'features/mapping/components/MarkerEditControls';
import { MarkerEditGhost } from 'features/mapping/components/MarkerEditGhost';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { useFormContext, useWatch } from 'react-hook-form';
import { useMemo } from 'react';
import { useDefaultMapCenter } from 'hooks/useDefaultMapCenter';
import { LatLng } from 'shared/types';
import { WildfireMarker } from 'app/pages/CreateEditWildfire/WildfireForm/components/WildfireMarker';
import { FormValues } from '../../EditAutomatedGeoEventForm.utils';

type EditAutomatedGeoEventFormMapProps = {
  geoEvent: Wildfire;
  markerLock: UseMarkerEditLockResult;
  zoom?: number;
  center?: LatLng;
};

export const EditAutomatedGeoEventFormMap = (
  props: EditAutomatedGeoEventFormMapProps,
): JSX.Element => {
  const { geoEvent, markerLock, zoom, center } = props;
  const { control } = useFormContext<FormValues>();
  const [status, lat, lng] = useWatch({
    control,
    name: ['status', 'lat', 'lng'],
  });

  // A staged move is previewed without being written to the form yet.
  const displayLat = markerLock.stagedLatLng?.lat ?? lat;
  const displayLng = markerLock.stagedLatLng?.lng ?? lng;

  // While editing, a click anywhere on the map repositions the marker.
  const isMarkerEditActive = markerLock.enabled && !markerLock.isLocked;

  const anchor = geoEvent.data?.isComplexParent ? 'center' : 'bottom';

  const defaultMapCenter = useDefaultMapCenter({
    geoEvent: center ?? geoEvent,
  });

  const currentGeoEvent = useMemo(
    () => ({
      ...geoEvent,
      isActive: status === 'active',
      lat: displayLat,
      lng: displayLng,
    }),
    [geoEvent, status, displayLat, displayLng],
  );

  const ghostFootprint = useMemo(
    () =>
      iconFootprint(
        getWildfireGeoEventIcon(geoEvent),
        anchor,
        getWildfireEventScale(geoEvent),
      ),
    [geoEvent, anchor],
  );

  return (
    <Map
      center={defaultMapCenter}
      zoom={zoom}
      mapType={MapType.AutomatedGeoEventForm}
      showSearch
      searchDisabled={markerLock.isLocked}
      searchIgnoresSelectedGeoEvent
      searchBarAdornment={<MarkerEditControls lock={markerLock} />}
      cursor={isMarkerEditActive ? 'crosshair' : undefined}
      onLocationChange={markerLock.handleLocationChange}
      searchApi="google"
    >
      <WildfireMarker
        geoEvent={currentGeoEvent}
        onChange={markerLock.handleLocationChange}
        locked={markerLock.isLocked}
        moveOnEvacZoneClick
      />
      <MarkerEditGhost
        markerLock={markerLock}
        original={geoEvent}
        footprint={ghostFootprint}
      >
        <Map.WildFireGeoEventMarker
          geoEvent={geoEvent}
          draggable={false}
          opacityOverride={0.5}
          anchor={anchor}
        />
      </MarkerEditGhost>
    </Map>
  );
};
